import {
  Project,
  ProjectCreateRequestBody,
  ProjectCreateResponse,
  ProjectListResponse,
  ProjectWithActivities,
  RDTIResponses,
  ResearchActivity,
  UserRole,
} from '@avanzu/types';
import {
  carryForwardResponses,
  getCarryForwardMap,
  resolveSchemaVersionForTaxYear,
} from '@avanzu/rdti-schema';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { ScienceOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services';
import ProjectEditor from './components/ProjectEditor';
import ProjectSelector from './components/ProjectSelector';

export const ProjectPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

export const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  const { projectId } = useParams<{ projectId: string }>();
  const { organisationId } = useAuth();
  const { refreshCurrentProject, currentProjectId } = useProject();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (organisationId) {
      fetchProjects();
    }
  }, [organisationId, projectId]);

  // Handle projectId from URL params
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find((p) => p.id === projectId);
      if (project) {
        setSelectedProject(project);
      }
    }
  }, [projectId, projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);

      const response = await apiClient.get(`/organisations/${organisationId}/projects`);
      const data: ProjectListResponse = await response.json();

      if (data.success) {
        setProjects(data.projects);
      } else {
        enqueueSnackbar(data.message || 'Failed to fetch projects', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to fetch projects', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
  };

  const handleProjectEdit = (project: Project) => {
    setSelectedProject(project);
  };

  const handleCreateProject = async (taxYear?: string, carryForwardFromProjectId?: string) => {
    if (!organisationId) return;

    try {
      const getCurrentTaxYear = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const taxYearStart = currentYear;
        return `${taxYearStart}-${(taxYearStart + 1).toString().slice(-2)}`;
      };

      const finalTaxYear = taxYear || getCurrentTaxYear();
      const targetVersion = resolveSchemaVersionForTaxYear(finalTaxYear);

      // If carry-forward is requested, fetch the source project + activities
      // and pre-compute the responses payload so we can send it on create.
      let carriedProjectResponses: RDTIResponses | undefined;
      let carriedActivities: Array<Partial<ResearchActivity>> | undefined;
      if (carryForwardFromProjectId) {
        try {
          const srcResp = await apiClient.get(
            `/organisations/${organisationId}/projects/${carryForwardFromProjectId}`,
          );
          const srcData = (await srcResp.json()) as { success: boolean; project?: ProjectWithActivities };
          if (srcData.success && srcData.project) {
            const src = srcData.project;
            const sourceVersion = src.schemaVersion ?? resolveSchemaVersionForTaxYear(src.taxYear);
            if (getCarryForwardMap(sourceVersion, targetVersion)) {
              carriedProjectResponses = carryForwardResponses(
                sourceVersion,
                targetVersion,
                'project',
                src.responses ?? {},
              );
              carriedActivities = (src.activities ?? []).map((a) => ({
                name: a.name,
                type: a.type,
                budgetAmount: a.budgetAmount,
                responses: carryForwardResponses(
                  sourceVersion,
                  targetVersion,
                  a.type === 'core' ? 'coreActivity' : 'supportingActivity',
                  a.responses ?? {},
                ),
              }));
            }
          }
        } catch (e) {
          // Non-fatal — proceed without carry-forward.
          console.warn('Carry-forward source fetch failed', e);
        }
      }

      const requestBody: ProjectCreateRequestBody & { responses?: RDTIResponses } = {
        name: 'New R&D Project',
        taxYear: finalTaxYear,
        totalBudget: 0,
        ...(carriedProjectResponses ? { responses: carriedProjectResponses } : {}),
      };

      const response = await apiClient.post(
        `/organisations/${organisationId}/projects`,
        requestBody
      );

      if (response.status === 402) {
        const err = await response.json().catch(() => ({} as any));
        if (err.code === 'payment_requires_action') {
          enqueueSnackbar(
            'Payment requires confirmation — please verify with your bank, then try again',
            { variant: 'warning' }
          );
        } else {
          enqueueSnackbar(
            err.error || 'Payment required to create this project — please check your payment method or upgrade your tier',
            { variant: 'error' }
          );
        }
        return;
      }

      const data: ProjectCreateResponse = await response.json();

      if (data.success && data.project) {
        const newProject = data.project;

        // If we carried forward, create the activities too (best-effort —
        // each one is independent; a single failure doesn't block the rest).
        if (carriedActivities && carriedActivities.length > 0) {
          await Promise.all(
            carriedActivities.map((a) =>
              apiClient.post(
                `/organisations/${organisationId}/projects/${newProject.id}/activities`,
                a,
              ).catch((e) => console.warn('Carry-forward activity create failed', e)),
            ),
          );
        }

        setProjects([newProject, ...projects]);
        setSelectedProject(newProject);
        enqueueSnackbar(
          carryForwardFromProjectId
            ? 'Project created — review pre-filled fields and add answers to new questions'
            : 'Project created successfully',
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar(data.message || 'Failed to create project', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to create project', {
        variant: 'error',
      });
    }
  };

  const handleProjectUpdate = (updatedProject: Project) => {
    setProjects((prev) =>
      prev.map((project) => (project.id === updatedProject.id ? updatedProject : project))
    );
    setSelectedProject(updatedProject);
    if (updatedProject.id === currentProjectId) {
      refreshCurrentProject();
    }
    enqueueSnackbar('Project saved', { variant: 'success' });
  };

  const handleProjectDelete = (deletedProject: Project) => {
    setProjects((prev) => prev.filter((project) => project.id !== deletedProject.id));
    if (selectedProject?.id === deletedProject.id) {
      setSelectedProject(
        projects.length > 1 ? projects.find((p) => p.id !== deletedProject.id) || null : null
      );
    }
    enqueueSnackbar('Project deleted successfully', { variant: 'success' });
  };

  if (!organisationId) {
    return (
      <ContentWrapper>
        <EmptyStateContent
          icon={<ScienceOutlined />}
          title="No organisation selected"
          message="No organisation selected. Please select an organisation to continue."
        />
      </ContentWrapper>
    );
  }

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="Research & Development Projects">
          <LoadingComponent context="Loading projects..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title="Research & Development Projects"
        action={
          <Button
            variant="contained"
            onClick={() => navigate('/app/projects/generate')}
            startIcon={<AutoFixHighIcon />}
          >
            Generate new project with AI
          </Button>
        }
      >
        <Box
          sx={{
            display: 'flex',
            minHeight: 'calc(100vh - 200px)',
            gap: 2,
          }}
        >
          {/* Left Panel - Project List */}
          <Box sx={{ width: '400px', flexShrink: 0 }}>
            <ProjectSelector
              projects={projects}
              selectedProject={selectedProject}
              onProjectSelect={handleProjectSelect}
              onCreateProject={handleCreateProject}
              onProjectEdit={handleProjectEdit}
              loading={loading}
            />
          </Box>

          {/* Right Panel - Project Editor */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <ProjectEditor
              project={selectedProject}
              projects={projects}
              onProjectUpdate={handleProjectUpdate}
              onProjectDelete={handleProjectDelete}
            />
          </Box>
        </Box>
      </PaperSection>
    </ContentWrapper>
  );
};

export default Projects;
